/**
 * KeywordReviewPanel
 *
 * Core interactive panel for reviewing negative keyword recommendations.
 * Extracted from the mardi agent OutputScreen to be shared between:
 * - The full mardi agent page (OutputScreen)
 * - The keyword_review behaviour slot (inline in chat)
 *
 * Features:
 * - Category sidebar with filtering
 * - DataTable with inline editing, sorting, and row selection
 * - Bulk approve/reject actions
 * - Footer with approval stats, discard, and upload buttons
 * - Upload confirmation dialog
 * - Loading, empty, completed, and category-completed states
 *
 * The panel fetches its own data via useApprovals and manages all internal state.
 * Consumers only need to provide the workflowInstanceId and optional configuration.
 */

"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Badge,
  DataTable,
} from "@strivelabs/ui-core";
import {
  Download,
  SearchCheck,
  Upload,
  Info,
  X,
  Menu,
  Pencil,
  Check,
  Loader2,
} from "lucide-react";
import type { CellContext, RowSelectionState } from "@tanstack/react-table";
import {
  useApprovals,
  useUpdateApprovalItem,
  useUpdateApproval,
} from "@/hooks/useApprovals";
import { log } from "@/lib/logger";
import { useAppStore } from "@/store/appStore";
import { CategorySidebar } from "./CategorySidebar";
import { formatNumber, formatPercent, formatCurrency } from "./formatters";
import { escapeCSV, capitalizeWords, getCurrencySymbol } from "./utils";
import {
  getCategoryDetail,
  APPROVAL_STATUS,
  MatchType,
  MatchTypeLabel,
  UploadLevel,
  defaultMatchTypeStyles,
} from "./constants";
import type { ApprovalItem, NegativeKeyword } from "./types";

export interface KeywordReviewPanelProps {
  /** Workflow instance ID to fetch approvals for */
  workflowInstanceId: string;
  /** When true, adjusts layout for compact slot context (shorter heights, no sidebar, no CSV download) */
  compact?: boolean;
  /** When true, disables all interactive elements (for read-only historical chat messages) */
  readOnly?: boolean;
  /** Custom React node rendered in the empty state (e.g., "Start Analysis" button) */
  emptyStateAction?: React.ReactNode;
  /**
   * Optional category key(s) to pre-filter keywords.
   * Accepts a single category string or an array for multiple categories.
   * When provided, only keywords matching the given category/categories are shown
   * and the sidebar is hidden.
   * When omitted, all keywords are shown in a flat table (slot) or
   * the full category sidebar is available (agent page).
   */
  categoryFilter?: string | string[];
  /**
   * Optional mock approval items for local UI testing.
   * When provided, the panel uses this data instead of fetching from the API.
   * Mutations (approve/reject/upload) still fire against the real API — only
   * the initial data source is overridden.
   */
  mockApprovalItems?: ApprovalItem[];
  /**
   * Controlled row selection state (TanStack RowSelectionState).
   * When provided, the panel uses this instead of its own internal state,
   * allowing selection to be shared across compact/expanded views.
   */
  rowSelection?: RowSelectionState;
  /** Callback when row selection state changes (controlled mode) */
  onRowSelectionChange?: (selection: RowSelectionState) => void;
}

/** Inline editable search term sub-component — extracted to preserve React identity across renders */
function EditableSearchTerm({
  keyword,
  searchTerm,
  onSave,
  onCancel,
}: {
  keyword: NegativeKeyword;
  searchTerm: string;
  onSave: (keyword: NegativeKeyword, newSearchTerm: string) => void;
  onCancel: () => void;
}) {
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.getElementById(
        `edit-input-${keyword.id}`
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [keyword.id]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSave(keyword, inputValue);
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div className="flex items-center">
      <input
        id={`edit-input-${keyword.id}`}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full p-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex ml-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onSave(keyword, inputValue)}
          className="h-6 w-6 p-0 text-green-600 hover:text-green-800"
          title="Save"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="h-6 w-6 p-0 text-red-600 hover:text-red-800 ml-1"
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Maps backend match type codes to human-readable display labels.
 */
const getMatchTypeDisplay = (matchType: string): string => {
  const matchTypeMap: Record<string, string> = {
    "BROAD": "Broad Match",
    "PHRASE": "Phrase Match",
    "NEAR_PHRASE": "Phrase Match (close variant)",
    "EXACT": "Exact Match",
    "NEAR_EXACT": "Exact Match (close variant)",
    "PERFORMANCE_MAX": "Performance Match",
  };
  return matchTypeMap[matchType] || matchType;
};

export const KeywordReviewPanel: React.FC<KeywordReviewPanelProps> = ({
  workflowInstanceId,
  compact = false,
  readOnly = false,
  emptyStateAction,
  categoryFilter,
  mockApprovalItems,
  rowSelection: controlledRowSelection,
  onRowSelectionChange: controlledOnRowSelectionChange,
}) => {
  // ── Internal state ──────────────────────────────────────────────────
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

  // Use controlled selection if provided, otherwise internal state
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const setRowSelection = controlledOnRowSelectionChange ?? setInternalRowSelection;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loadingKeywordId, setLoadingKeywordId] = useState<string | null>(null);
  const [editingKeywordId, setEditingKeywordId] = useState<string | null>(null);

  // ── Tenant settings (currency & upload config) ──────────────────────
  const tenantDetails = useAppStore((state) => state.tenantDetails);
  const currencyCode =
    (tenantDetails?.tenant?.settings?.account as { currency_code?: string })
      ?.currency_code || "USD";

  const uploadConfig = tenantDetails?.tenant?.settings?.upload as
    | { default_match_type?: string; level?: string; name?: string }
    | undefined;
  const uploadMatchType = uploadConfig?.default_match_type || MatchType.PHRASE;
  const uploadLevel = uploadConfig?.level || UploadLevel.CAMPAIGN;

  // ── Data fetching (skipped when mockApprovalItems is provided) ──────
  const hasMockData = !!mockApprovalItems;
  const {
    data: approvalsData,
    refetch: refetchApprovals,
    isLoading: isApprovalsLoading,
  } = useApprovals(
    {
      page_size: 1,
      sort_by: "id",
      sort_order: "desc",
      workflow_instance_id: workflowInstanceId,
    },
    { enabled: !!workflowInstanceId && !hasMockData }
  );

  // ── Mutations ───────────────────────────────────────────────────────
  const updateApprovalItemMutation = useUpdateApprovalItem({
    onSuccess: () => {
      refetchApprovals();
      setEditingKeywordId(null);
      setLoadingKeywordId(null);
    },
    onError: (error) => {
      log("error", "Failed to update keyword status", { error });
      setEditingKeywordId(null);
      setLoadingKeywordId(null);
    },
  });

  const updateApprovalMutation = useUpdateApproval({
    onSuccess: () => {
      const approvedCount = negativeKeywords.filter(
        (k) =>
          k.status === APPROVAL_STATUS.APPROVED ||
          k.status === APPROVAL_STATUS.REJECTED
      ).length;

      refetchApprovals();
      setDialogOpen(false);
      setRowSelection({});

      toast({
        title: "Upload to Google Ads Successful",
        description: `${approvedCount} ${approvedCount === 1 ? "keyword has" : "keywords have"} been uploaded to Google Ads.`,
        variant: "success",
      });
    },
    onError: (error) => {
      log("error", "Failed to update approval status", { error });

      toast({
        title: "Upload to Google Ads Failed",
        description:
          "Failed to upload keywords to Google Ads. Please try again later.",
        variant: "destructive",
      });
    },
  });

  // ── Data transformation ─────────────────────────────────────────────
  const negativeKeywords: NegativeKeyword[] = useMemo(() => {
    // Use mock items when provided, otherwise fall back to API data
    const items: ApprovalItem[] | undefined = hasMockData
      ? mockApprovalItems
      : approvalsData?.approvals?.[0]?.approval_items;

    if (!items || items.length === 0) return [];

    const approvalId = hasMockData
      ? mockApprovalItems![0]?.approval_id ?? "mock-approval"
      : approvalsData!.approvals[0].id;

    return items.map((item: ApprovalItem) => {
      const hasValidReviewedData =
        item.reviewed_data &&
        Object.keys(item.reviewed_data).length > 0 &&
        item.reviewed_data.search_term;

      const dataSource = hasValidReviewedData
        ? item.reviewed_data
        : item.data;

      return {
        id: item.id,
        search_term: dataSource.search_term || "",
        keyword: dataSource.keyword || "",
        match_type: dataSource.match_type || "",
        cost: dataSource.cost || 0,
        score: dataSource.score || 0,
        category_filter: dataSource.category_filter || "",
        justification: dataSource.justification || "",
        conv_rate: dataSource.conv_rate || 0,
        cost_per_conv: dataSource.cost_per_conv || 0,
        impressions: dataSource.impr || 0,
        interaction_rate: dataSource.interaction_rate || 0,
        campaign: dataSource.campaign || "",
        approval_id: approvalId,
        status: item.status,
      };
    });
  }, [approvalsData, hasMockData, mockApprovalItems]);

  // ── Filtering & grouping ────────────────────────────────────────────
  const pendingKeywords = useMemo(() => {
    return negativeKeywords.filter(
      (keyword) =>
        keyword.status === null || keyword.status === APPROVAL_STATUS.IGNORED
    );
  }, [negativeKeywords]);

  const categoryGroups = useMemo(() => {
    const groups: Record<string, NegativeKeyword[]> = {};
    pendingKeywords.forEach((keyword) => {
      const category = keyword.category_filter || "uncategorized";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(keyword);
    });
    return groups;
  }, [pendingKeywords]);

  const categories = useMemo(() => {
    return Object.entries(categoryGroups).map(([name, keywords]) => ({
      name,
      count: keywords.length,
      selected: name === selectedCategory,
    }));
  }, [categoryGroups, selectedCategory]);

  // ── Derived state & effects ─────────────────────────────────────────
  const approvalStatus = useMemo(() => ({
    approved: negativeKeywords.filter(
      (k) => k.status === APPROVAL_STATUS.APPROVED
    ).length,
    rejected: negativeKeywords.filter(
      (k) => k.status === APPROVAL_STATUS.REJECTED
    ).length,
  }), [negativeKeywords]);

  const hasPendingKeywords = useMemo(() => {
    return Object.values(categoryGroups).some((group) => group.length > 0);
  }, [categoryGroups]);

  useEffect(() => {
    if (!hasPendingKeywords) {
      setSelectedCategory(null);
    }
  }, [hasPendingKeywords]);

  useEffect(() => {
    if (
      categories.length > 0 &&
      selectedCategory &&
      !categories.some((cat) => cat.name === selectedCategory)
    ) {
      setSelectedCategory(categories[0].name);
    }
  }, [categories, selectedCategory]);

  const filteredKeywords = useMemo(() => {
    if (!hasPendingKeywords) {
      return [];
    }

    // When a categoryFilter is provided (from behaviour data), apply it
    // regardless of compact/expanded mode — only show matching keywords.
    if (categoryFilter) {
      const all = Object.values(categoryGroups).flat();
      const filters = Array.isArray(categoryFilter) ? categoryFilter : [categoryFilter];
      return all.filter((k) => filters.includes(k.category_filter));
    }

    // In compact (slot) mode without categoryFilter: show all keywords flat.
    if (compact) {
      return Object.values(categoryGroups).flat();
    }

    // In full (agent page) mode: filter by the selected sidebar category.
    if (!selectedCategory) {
      return Object.values(categoryGroups).flat();
    }
    return categoryGroups[selectedCategory] || [];
  }, [selectedCategory, categoryGroups, hasPendingKeywords, compact, categoryFilter]);

  // Derive selected rows from the shared rowSelection state and visible keywords.
  // This replaces a local useState so both compact and expanded panels
  // compute the same value from the single shared rowSelection.
  const selectedRows = useMemo(() => {
    return filteredKeywords.filter((k) => rowSelection[k.id]);
  }, [filteredKeywords, rowSelection]);

  const showSelectionActions = selectedRows.length > 0;

  // ── Handlers ────────────────────────────────────────────────────────

  /** Export all keywords as CSV */
  const downloadCSV = () => {
    const headers = [
      "Search Term",
      "Matched Keyword",
      "Match Type",
      "Cost",
      "Confidence Score",
      "Category",
      "Justification",
      "Conversion Rate (%)",
      "Cost Per Conversion",
      "Impressions",
      "Interaction Rate (%)",
      "Approval",
    ];

    const csvContent = [
      headers.map((h) => escapeCSV(h)).join(","),
      ...negativeKeywords.map((k) =>
        [
          escapeCSV(k.search_term),
          escapeCSV(k.keyword),
          escapeCSV(getMatchTypeDisplay(k.match_type)),
          escapeCSV(k.cost),
          escapeCSV(k.score),
          escapeCSV(capitalizeWords(k.category_filter)),
          escapeCSV(k.justification),
          escapeCSV(k.conv_rate),
          escapeCSV(k.cost_per_conv),
          escapeCSV(k.impressions),
          escapeCSV(k.interaction_rate),
          escapeCSV(
            k.status === APPROVAL_STATUS.PENDING ||
              k.status === APPROVAL_STATUS.APPROVED
              ? APPROVAL_STATUS.APPROVED
              : APPROVAL_STATUS.REJECTED
          ),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `negative-keywords.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  /** Save an inline-edited search term */
  const handleSaveEdit = useCallback((keyword: NegativeKeyword, newSearchTerm: string) => {
    if (newSearchTerm === keyword.search_term) {
      setEditingKeywordId(null);
      return;
    }

    setLoadingKeywordId(keyword.id);

    const reviewedData = {
      search_term: newSearchTerm,
      keyword: keyword.keyword,
      match_type: keyword.match_type,
      cost: keyword.cost,
      score: keyword.score,
      category_filter: keyword.category_filter,
      justification: keyword.justification,
      conv_rate: keyword.conv_rate,
      cost_per_conv: keyword.cost_per_conv,
      impr: keyword.impressions,
      interaction_rate: keyword.interaction_rate,
    };

    updateApprovalItemMutation.mutate({
      approvalId: keyword.approval_id,
      approvalItemId: keyword.id,
      status: keyword.status || APPROVAL_STATUS.APPROVED,
      reviewedData: reviewedData,
    });
  }, [updateApprovalItemMutation]);

  /** Table column definitions */
  const columnDefs = useMemo(() => {
    return [
      {
        accessorKey: "search_term",
        header: "Search Term",
        enableSorting: true,
        minSize: 150,
        size: 180,
        cell: ({ row }: CellContext<NegativeKeyword, unknown>) => {
          const keyword = row.original;
          const isEditing = editingKeywordId === keyword.id;
          const isLoading = loadingKeywordId === keyword.id;
          const searchTerm = row.getValue("search_term") as string;

          const isTableBusy =
            editingKeywordId !== null || loadingKeywordId !== null;
          const isThisItemBusy = isEditing || isLoading;

          if (isEditing && !readOnly) {
            return (
              <EditableSearchTerm
                keyword={keyword}
                searchTerm={searchTerm}
                onSave={handleSaveEdit}
                onCancel={() => setEditingKeywordId(null)}
              />
            );
          }

          return (
            <div className="flex items-center justify-between group">
              <span
                className={
                  isTableBusy && !isThisItemBusy ? "text-gray-400" : ""
                }
              >
                {searchTerm}
              </span>
              {!isTableBusy && !readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingKeywordId(keyword.id);
                  }}
                  className="h-6 w-6 p-0 ml-2 transition-colors hover:bg-gray-100 rounded-full"
                >
                  <Pencil className="h-4 w-4 text-gray-500" />
                </Button>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "keyword",
        header: "Matched Keyword",
        enableSorting: false,
        minSize: 150,
        size: 180,
      },
      {
        accessorKey: "match_type",
        header: "Match Type",
        enableSorting: false,
        minSize: 150,
        size: 180,
        cell: ({ row }: CellContext<NegativeKeyword, unknown>) => {
          const matchType = row.getValue("match_type") as string;
          const displayText = getMatchTypeDisplay(matchType);
          const style = defaultMatchTypeStyles[matchType];

          if (!style) {
            return <Badge variant="outline">{displayText}</Badge>;
          }

          return (
            <Badge
              variant="outline"
              className={`${style.background} ${style.text} ${style.border} border font-medium`}
            >
              {displayText}
            </Badge>
          );
        },
      },
      {
        accessorKey: "justification",
        header: "Justification",
        enableSorting: false,
        minSize: 200,
        size: 250,
      },
      {
        accessorKey: "cost",
        header: "Cost",
        enableSorting: true,
        sortDescFirst: true,
        minSize: 100,
        size: 120,
        cell: ({ row }: CellContext<NegativeKeyword, unknown>) => {
          return formatCurrency(
            row.getValue("cost") as number,
            2,
            getCurrencySymbol(currencyCode)
          );
        },
      },
      {
        id: "info",
        header: "",
        enableSorting: false,
        size: 50,
        cell: ({ row }: CellContext<NegativeKeyword, unknown>) => {
          const keyword = row.original;
          const additionalInfo = (
            <div className="space-y-4 max-w-xs">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Performance Metrics</h4>
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-muted-foreground">
                    Confidence Score:
                  </span>
                  <span>{formatPercent(keyword.score)}</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-muted-foreground">Impressions:</span>
                  <span>{formatNumber(keyword.impressions)}</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-muted-foreground">
                    Conversion Rate:
                  </span>
                  <span>{formatPercent(keyword.conv_rate / 100)}</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-muted-foreground">
                    Cost Per Conversion:
                  </span>
                  <span>
                    {formatCurrency(
                      keyword.cost_per_conv,
                      2,
                      getCurrencySymbol(currencyCode)
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-muted-foreground">
                    Interaction Rate:
                  </span>
                  <span>
                    {formatPercent(keyword.interaction_rate / 100)}
                  </span>
                </div>
              </div>
            </div>
          );

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center cursor-help">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="border bg-white text-sm text-popover-foreground shadow-md">
                  {additionalInfo}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
    ];
  }, [loadingKeywordId, editingKeywordId, currencyCode, readOnly, handleSaveEdit]);

  /** Approve or reject all selected keywords at once */
  const handleBulkStatusUpdate = async (
    status: typeof APPROVAL_STATUS.APPROVED | typeof APPROVAL_STATUS.REJECTED
  ) => {
    if (selectedRows.length === 0 || editingKeywordId !== null) return;

    const updatePromises = selectedRows.map((keyword) =>
      updateApprovalItemMutation.mutateAsync({
        approvalId: keyword.approval_id,
        approvalItemId: keyword.id,
        status,
      })
    );

    try {
      await Promise.all(updatePromises);
      setRowSelection({});

      toast({
        title: "Bulk Status Update Successful",
        description: `${selectedRows.length} ${selectedRows.length === 1 ? "keyword" : "keywords"} ${status === APPROVAL_STATUS.APPROVED ? "approved" : "rejected"} successfully.`,
        variant: "success",
      });
    } catch (error) {
      log("error", "Failed to update keyword statuses in bulk", { error });

      toast({
        title: "Bulk Status Update Failed",
        description: `Failed to ${status === APPROVAL_STATUS.APPROVED ? "approve" : "reject"} some or all of the selected keywords. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleShowUploadDialog = () => {
    if (editingKeywordId !== null) return;
    setDialogOpen(true);
  };

  /** Reset all approved/rejected items back to IGNORED */
  const handleDiscardChanges = async () => {
    const keywordsToReset = negativeKeywords.filter(
      (keyword) =>
        keyword.status === "APPROVED" || keyword.status === "REJECTED"
    );

    if (keywordsToReset.length === 0) {
      toast({
        title: "No Changes to Discard",
        description: "There are no approved or rejected keywords to reset.",
        variant: "warning",
      });
      return;
    }

    toast({
      title: "Discarding Feedback",
      description: `Reloading ${keywordsToReset.length} ${keywordsToReset.length === 1 ? "keyword" : "keywords"}...`,
      variant: "default",
    });

    const resetPromises = keywordsToReset.map((keyword) =>
      updateApprovalItemMutation.mutateAsync({
        approvalId: keyword.approval_id,
        approvalItemId: keyword.id,
        status: "IGNORED",
      })
    );

    try {
      await Promise.all(resetPromises);
      setRowSelection({});

      toast({
        title: "Feedbacks Discarded",
        description: `Successfully reloaded ${keywordsToReset.length} ${keywordsToReset.length === 1 ? "keyword" : "keywords"} for review.`,
        variant: "success",
      });
    } catch (error) {
      log("error", "Failed to discard feedback", { error });

      toast({
        title: "Failed to Discard Feedback",
        description:
          "An error occurred while reloading keywords. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getApprovedKeywords = () => {
    return negativeKeywords.filter(
      (keyword) =>
        keyword.status === APPROVAL_STATUS.APPROVED ||
        keyword.status === APPROVAL_STATUS.REJECTED
    );
  };

  /** Finalize the approval by setting status to COMPLETED (triggers upload) */
  const handleConfirmUpload = () => {
    const approvalId = negativeKeywords[0]?.approval_id;

    if (!approvalId) {
      toast({
        title: "Error",
        description: "No valid approval ID found.",
        variant: "destructive",
      });
      return;
    }

    updateApprovalMutation.mutate({
      approvalId,
      status: "COMPLETED",
    });
    // refetchApprovals() is already called in updateApprovalMutation.onSuccess
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setRowSelection({});
  };

  // ── Derived display values ──────────────────────────────────────────
  const isUploadButtonDisabled =
    (approvalStatus.approved === 0 && approvalStatus.rejected === 0) ||
    editingKeywordId !== null ||
    readOnly;
  const isDiscardButtonDisabled =
    (approvalStatus.approved === 0 && approvalStatus.rejected === 0) ||
    editingKeywordId !== null ||
    readOnly;

  let uploadButtonTooltip = "";
  if (readOnly) {
    uploadButtonTooltip = "Actions are disabled in read-only mode";
  } else if (editingKeywordId !== null) {
    uploadButtonTooltip =
      "Please complete the current editing operation before uploading";
  } else if (
    approvalStatus.approved === 0 &&
    approvalStatus.rejected === 0
  ) {
    uploadButtonTooltip =
      "You need to approve or reject at least one keyword before uploading";
  }

  const discardButtonTooltip = readOnly
    ? "Actions are disabled in read-only mode"
    : editingKeywordId !== null
      ? "Please complete the current editing operation before discarding"
      : approvalStatus.approved === 0 && approvalStatus.rejected === 0
        ? "No changes to discard"
        : "";

  // Height for the table area — shorter in compact (slot) mode
  const tableHeight = compact
    ? "calc(60vh - 200px)"
    : "calc(100vh - 250px)";
  const emptyStateMinHeight = compact
    ? "min-h-[200px]"
    : "min-h-[300px]";

  // ── Loading state (skipped when using mock data) ────────────────────
  if (isApprovalsLoading && !hasMockData) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${emptyStateMinHeight}`}>
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-muted-foreground">Loading keyword data...</p>
      </div>
    );
  }

  // ── Empty / completed state ─────────────────────────────────────────
  if (
    negativeKeywords.length === 0 ||
    (!hasMockData && approvalsData?.approvals[0]?.status === "COMPLETED")
  ) {
    return (
      <div className={`flex flex-col items-center justify-center ${emptyStateMinHeight} text-center p-6`}>
        <SearchCheck className="h-8 w-8 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">A Clean Slate!</h3>
        <p className="text-muted-foreground mb-6">
          No keywords awaiting your review.
        </p>
        {emptyStateAction}
      </div>
    );
  }

  // ── Main review UI ──────────────────────────────────────────────────

  // Derive the category title bar label.
  // When categoryFilter is active, show the filtered category name(s).
  // Otherwise in full mode, show the sidebar-selected category.
  const activeCategoryLabel = (() => {
    if (categoryFilter) {
      const filters = Array.isArray(categoryFilter) ? categoryFilter : [categoryFilter];
      return filters.map((f) => getCategoryDetail(f).displayName).join(", ");
    }
    if (compact) return null;
    return selectedCategory ? getCategoryDetail(selectedCategory).displayName : null;
  })();
  const showCategoryTitle = !!activeCategoryLabel;

  return (
    <>
      {/* Global action buttons (download CSV) — hidden in compact (slot) mode */}
      {!readOnly && !compact && (
        <div className="mx-3 my-4 md:m-6 flex justify-end items-center space-x-4">
          <Button
            variant="secondary"
            onClick={downloadCSV}
            className="w-full sm:w-auto"
          >
            <Download className="mr-2 h-4 w-4" />
            <span className="whitespace-nowrap">Download CSV</span>
          </Button>
        </div>
      )}

      {/* Main content with optional sidebar and table */}
      <div className={`relative flex flex-col md:flex-row overflow-hidden max-w-full ${compact ? '' : 'border rounded-lg'}`}>
        {/* Mobile sidebar toggle button — hidden in compact mode or when categoryFilter is active */}
        {!compact && !categoryFilter && hasPendingKeywords && (
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-md"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {sidebarOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        )}

        {/* Category sidebar — hidden in compact (slot) mode or when categoryFilter is active */}
        {!compact && !categoryFilter && hasPendingKeywords && (
          <div
            className={`${sidebarOpen ? "block" : "hidden"} md:block w-full md:w-[20rem] md:min-w-[20rem] md:max-w-[20rem] border-b md:border-b-0 md:border-r overflow-hidden`}
          >
            <CategorySidebar
              categories={categories}
              onCategorySelect={handleCategorySelect}
              compact={compact}
            />
          </div>
        )}

        {/* Table content area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Category title bar — shows category name when sidebar-selected or categoryFilter active */}
          {hasPendingKeywords && showCategoryTitle && (
            <div className="flex justify-between items-center py-3 px-2 md:px-4 border-b">
              <div className="flex items-center">
                {filteredKeywords.length > 0 ? (
                  <h4 className="text-md font-medium break-words max-w-full">
                    {activeCategoryLabel}
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      ({filteredKeywords.length}{" "}
                      {filteredKeywords.length > 1 ? "keywords" : "keyword"})
                    </span>
                  </h4>
                ) : (
                  <h4 className="text-md font-medium break-words max-w-full">
                    {activeCategoryLabel}
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      (Category completed)
                    </span>
                  </h4>
                )}
              </div>
            </div>
          )}

          <div className="p-4 flex-1 overflow-auto">
            {/* Bulk selection actions bar */}
            {showSelectionActions && !readOnly && (
              <div className="flex items-center justify-between mb-4 p-2 bg-gray-50 rounded-md border">
                <div className="text-sm font-medium">
                  {selectedRows.length}{" "}
                  {selectedRows.length === 1 ? "keyword" : "keywords"} selected
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkStatusUpdate("APPROVED")}
                    className="text-green-600 border-green-600 hover:bg-green-50"
                  >
                    Approve Selected
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkStatusUpdate("REJECTED")}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    Reject Selected
                  </Button>
                </div>
              </div>
            )}

            {/* "All done" state — all pending keywords have been reviewed */}
            {!hasPendingKeywords ? (
              <div
                className="flex flex-col items-center justify-center text-center p-6"
                style={{ height: tableHeight }}
              >
                <SearchCheck className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Well Done!</h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  All recommendations have been reviewed. Score a win by
                  completing the upload.
                </p>
                {!readOnly && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              onClick={() => setDialogOpen(true)}
                              disabled={isUploadButtonDisabled}
                              className="w-full sm:w-auto"
                            >
                              <Upload className="mr-2 h-4 w-4" />
                              Upload to Google Ads
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {uploadButtonTooltip && (
                          <TooltipContent>
                            <p>{uploadButtonTooltip}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="outline"
                              onClick={handleDiscardChanges}
                              disabled={isDiscardButtonDisabled}
                              className="w-full sm:w-auto"
                            >
                              <X className="mr-2 h-4 w-4" />
                              Discard Feedback
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {discardButtonTooltip && (
                          <TooltipContent>
                            <p>{discardButtonTooltip}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            ) : filteredKeywords.length === 0 && selectedCategory ? (
              /* Category completed state */
              <div
                className="flex flex-col items-center justify-center text-center p-6"
                style={{ height: tableHeight }}
              >
                <SearchCheck className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  Category Completed
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  All keywords in this category have been processed. Please
                  select another category from the sidebar.
                </p>
              </div>
            ) : (
              /* Main data table */
              <DataTable
                variant="client"
                columns={columnDefs}
                data={filteredKeywords}
                enableSearch={false}
                pageSize={10}
                initialPage={0}
                enableRowSelection={editingKeywordId === null && !readOnly}
                getRowId={(row) => row.id}
                rowSelection={
                  editingKeywordId === null && !readOnly ? rowSelection : {}
                }
                onRowSelectionChange={
                  editingKeywordId === null && !readOnly
                    ? setRowSelection
                    : undefined
                }
                enableExport={false}
                bordered={false}
                horizontalScroll={true}
                height={tableHeight}
                excludedGlobalFilterColumns={["justification"]}
                initialSorting={[
                  { id: "cost", desc: true },
                  { id: "search_term", desc: false },
                ]}
                enablePagination={false}
              />
            )}
          </div>

          {/* Footer with approval stats + action buttons */}
          <div className="px-3 md:px-4 py-3 border-t sticky bottom-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 bg-gray-50">
            {/* Approval count indicators */}
            <div className="flex items-center space-x-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <span className="text-sm font-medium text-green-600 mr-1">
                        {approvalStatus.approved}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        approved
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md max-h-60 overflow-auto">
                    <div className="space-y-1">
                      {approvalStatus.approved > 0 ? (
                        negativeKeywords
                          .filter((k) => k.status === "APPROVED")
                          .map((k) => (
                            <div key={k.id} className="text-sm">
                              {k.search_term}
                            </div>
                          ))
                      ) : (
                        <div className="text-sm">No approved keywords</div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <span className="text-sm font-medium text-red-600 mr-1">
                        {approvalStatus.rejected}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        rejected
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md max-h-60 overflow-auto">
                    <div className="space-y-1">
                      {approvalStatus.rejected > 0 ? (
                        negativeKeywords
                          .filter((k) => k.status === "REJECTED")
                          .map((k) => (
                            <div key={k.id} className="text-sm">
                              {k.search_term}
                            </div>
                          ))
                      ) : (
                        <div className="text-sm">No rejected keywords</div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Discard + Upload buttons */}
            {!readOnly && (
              <div className="flex items-center space-x-2 w-full sm:w-auto">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          onClick={handleDiscardChanges}
                          variant="outline"
                          disabled={isDiscardButtonDisabled}
                          className="w-full sm:w-auto"
                        >
                          <X className="mr-2 h-4 w-4" />
                          <span className="whitespace-nowrap">
                            Discard Feedback
                          </span>
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {discardButtonTooltip && (
                      <TooltipContent>
                        <p>{discardButtonTooltip}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          onClick={handleShowUploadDialog}
                          variant="default"
                          disabled={isUploadButtonDisabled}
                          className="w-full sm:w-auto"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          <span className="whitespace-nowrap">
                            Upload to Google Ads
                          </span>
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {uploadButtonTooltip && (
                      <TooltipContent>
                        <p>{uploadButtonTooltip}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload confirmation dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm &amp; Upload</DialogTitle>
            <DialogDescription>
              You are uploading{" "}
              <span className="font-semibold">
                {getApprovedKeywords()?.length}
              </span>{" "}
              {getApprovedKeywords()?.length === 1 ? "negative" : "negatives"}{" "}
              as {MatchTypeLabel[uploadMatchType as MatchType]} to the{" "}
              <span className="font-semibold">
                {uploadLevel === UploadLevel.NEGATIVE_KEYWORDS_LIST
                  ? uploadConfig?.name
                  : "campaign"}
              </span>
              <span>
                {uploadLevel === UploadLevel.NEGATIVE_KEYWORDS_LIST
                  ? " list"
                  : " campaign"}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleConfirmUpload}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KeywordReviewPanel;
